import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate, money, ordinal } from "@/lib/ui";
import useIsMobile from "@/lib/useIsMobile";
import usePersistentState from "@/lib/usePersistentState";
import { M } from "@/lib/mobileTheme";
import { Search, Loader2, Info, ChevronDown, ChevronRight } from "lucide-react";
import { CowIcon } from "@/components/icons/CowIcon";

function hasStock(ev) { return (ev.rows || []).some((r) => r.stock && r.stock.trim()); }

export default function Stock() {
  const m = useIsMobile();
  const [mode, setMode] = usePersistentState("rp_stock_mode", "season");
  const [q, setQ] = useState("");
  const [params, setParams] = useSearchParams();

  // Deep-link from My Riders: /stock?animal=<name> → jump to All stock, prefill search, auto-open.
  const [deepAnimal, setDeepAnimal] = useState(null);
  useEffect(() => {
    const a = params.get("animal");
    if (a) {
      setMode("all");
      setQ(a);
      setDeepAnimal(a);
      params.delete("animal");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const page = m ? "bg-[#0C1017] min-h-screen text-slate-200 pb-24" : "";
  const title = m ? "text-slate-100" : "text-[#14181F]";
  const inp = m ? "bg-white/5 border-white/10 text-slate-100 placeholder-slate-500" : "bg-white border-[#D7DBE0] text-[#14181F] placeholder-slate-400";

  return (
    <div className={page} data-testid="stock-page">
      <div className={`px-4 sm:px-6 py-5 border-b flex items-center gap-2 ${m ? "border-white/10" : "border-[#E4E7EB]"}`}>
        <CowIcon className="w-6 h-6 text-[#C8000F]" />
        <h1 className={`font-display text-2xl font-extrabold tracking-tight ${title}`}>Rodeo Stock</h1>
      </div>

      <div className="p-4 sm:p-6 max-w-4xl space-y-5">
        <div className="flex rounded-lg overflow-hidden border border-[#D7DBE0] w-fit" data-testid="stock-mode-toggle">
          <button onClick={() => setMode("season")} data-testid="stock-mode-season"
            className={`text-sm font-semibold px-4 py-2 ${mode === "season" ? "bg-[#C8000F] text-white" : (m ? "bg-white/5 text-slate-300" : "bg-white text-slate-600")}`}>Stock of the Season</button>
          <button onClick={() => setMode("all")} data-testid="stock-mode-all"
            className={`text-sm font-semibold px-4 py-2 ${mode === "all" ? "bg-[#C8000F] text-white" : (m ? "bg-white/5 text-slate-300" : "bg-white text-slate-600")}`}>All stock</button>
          <button onClick={() => setMode("rodeo")} data-testid="stock-mode-rodeo"
            className={`text-sm font-semibold px-4 py-2 ${mode === "rodeo" ? "bg-[#C8000F] text-white" : (m ? "bg-white/5 text-slate-300" : "bg-white text-slate-600")}`}>By rodeo</button>
        </div>

        {mode === "rodeo" ? <ByRodeo m={m} q={q} setQ={setQ} inp={inp} title={title} />
          : mode === "season" ? <StockOfSeason m={m} inp={inp} title={title} />
            : <AllStock m={m} q={q} setQ={setQ} inp={inp} title={title} autoOpen={deepAnimal} />}
      </div>
    </div>
  );
}

function TripDetail({ s, m, title }) {
  return (
    <div className={`px-3 pb-3 border-t ${m ? "border-white/5" : "border-[#EEF0F2]"}`}>
      {s.riders?.length > 0 && (
        <div className="pt-2.5">
          <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">Riders who've been on it</div>
          <div className="flex flex-wrap gap-1.5">
            {s.riders.map((rd, k) => (
              <span key={k} className={`text-[11px] px-2 py-1 rounded-md ${m ? "bg-white/5 border border-white/10 text-slate-200" : "bg-[#F1F3F5] border border-[#E4E7EB] text-slate-700"}`}>
                {rd.name}{rd.count > 1 && <span className="font-bold text-[#C8000F]"> ×{rd.count}</span>}
              </span>
            ))}
          </div>
        </div>
      )}
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mt-3 mb-1">Every trip this season</div>
      <div className="space-y-1.5">
        {s.appearances.map((a, j) => (
          <div key={j} className="flex items-center justify-between gap-2 text-xs">
            <div className="min-w-0">
              <span className={`font-medium ${title}`}>{a.contestant}</span>
              <span className="text-slate-400"> · {a.rodeo_name}</span>
              {ordinal(a.place) && <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#3B6B1E]/15 text-[#3B6B1E]">{ordinal(a.place)}</span>}
              {a.value && <span className="ml-1.5 font-bold text-[#3B6B1E]">{a.value}</span>}
              {a.amount_won > 0 && <span className="ml-1.5 font-bold text-[#E68A2E]">{money(a.amount_won)}</span>}
            </div>
            <span className="text-slate-400 shrink-0">{a.performance_label}{a.performance_date ? ` · ${fmtDate(a.performance_date)}` : ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StockOfSeason({ m, inp, title }) {
  const [data, setData] = useState(null);
  const [evFilter, setEvFilter] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);

  useEffect(() => { api.get("/stock?sort=score").then((r) => setData(r.data)).catch(() => setData({ stock: [], events: [] })); }, []);

  const list = useMemo(() => {
    if (!data) return [];
    const qq = q.trim().toLowerCase();
    return data.stock
      .filter((s) => (s.count || 0) >= 3 && s.avg_score != null)
      .filter((s) => (!evFilter || s.event === evFilter) && (!qq || s.stock.toLowerCase().includes(qq)));
  }, [data, q, evFilter]);

  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";
  const rankCls = (i) => i === 0 ? "bg-[#D4AF37] text-black" : i === 1 ? "bg-[#9CA3AF] text-black" : i === 2 ? "bg-[#B06B2B] text-white" : (m ? "bg-white/10 text-slate-300" : "bg-[#F1F3F5] text-slate-500");

  return (
    <>
      <div className={`rounded-xl border border-dashed p-3 text-xs text-slate-400 ${m ? "border-white/15" : "border-[#D7DBE0]"}`}>
        The rankest bulls &amp; broncs of the season by <b className={title}>average mark</b> — three trips or more. Tap any animal for its full season.
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search any animal by name or number…" data-testid="stock-season-search"
          className={`w-full rounded-lg border pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#C8000F] ${inp}`} />
      </div>

      {data && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="stock-season-event-filter">
          <button onClick={() => setEvFilter("")} className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border ${!evFilter ? "bg-[#C8000F] text-white border-[#C8000F]" : (m ? "bg-white/5 text-slate-300 border-white/10" : "bg-white text-slate-600 border-[#D7DBE0]")}`}>All events</button>
          {data.events.map((ev) => (
            <button key={ev} onClick={() => setEvFilter(ev)} data-testid={`stock-season-ev-${ev.replace(/\s/g, "-")}`}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border ${evFilter === ev ? "bg-[#C8000F] text-white border-[#C8000F]" : (m ? "bg-white/5 text-slate-300 border-white/10" : "bg-white text-slate-600 border-[#D7DBE0]")}`}>{ev}</button>
          ))}
        </div>
      )}

      {!data ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
      ) : (
        <>
          <div className="text-xs text-slate-400" data-testid="stock-season-count">{list.length} head ranked</div>
          <div className="space-y-2">
            {list.map((s, i) => {
              const isOpen = open === s.stock;
              return (
                <div key={i} className={`rounded-xl border ${card} ${i < 3 ? "ring-1 ring-[#D4AF37]/40" : ""}`} data-testid={`stock-season-item-${i}`}>
                  <button onClick={() => setOpen(isOpen ? null : s.stock)} className="w-full flex items-center gap-3 p-3 text-left">
                    <span className={`shrink-0 w-7 h-7 rounded-full text-[12px] font-extrabold flex items-center justify-center ${rankCls(i)}`}>{i + 1}</span>
                    <div className="shrink-0 text-right w-14">
                      <div className={`font-display font-extrabold text-xl leading-none ${title}`} data-testid={`stock-season-avg-${i}`}>{s.avg_score}</div>
                      <div className="text-[9px] font-bold uppercase tracking-wide text-slate-400">avg mark</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold text-sm truncate ${title}`}>{s.stock}</div>
                      <div className="text-[11px] text-slate-400">{s.event} · drawn {s.count}×{s.total_won > 0 ? ` · ${money(s.total_won)} won` : ""}</div>
                    </div>
                    {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                  </button>
                  {isOpen && <TripDetail s={s} m={m} title={title} />}
                </div>
              );
            })}
            {list.length === 0 && <div className="text-center py-12 text-slate-400">No animals with 3+ marked trips yet this season.</div>}
          </div>
        </>
      )}
    </>
  );
}

function AllStock({ m, q, setQ, inp, title, autoOpen }) {
  const [data, setData] = useState(null);
  const [evFilter, setEvFilter] = useState("");
  const [sort, setSort] = usePersistentState("rp_stock_sort", "count");
  const [open, setOpen] = useState(null);

  useEffect(() => { setData(null); api.get(`/stock?sort=${sort}`).then((r) => setData(r.data)).catch(() => setData({ stock: [], events: [] })); }, [sort]);

  const list = useMemo(() => {
    if (!data) return [];
    const qq = q.trim().toLowerCase();
    return data.stock.filter((s) => (!evFilter || s.event === evFilter) && (!qq || s.stock.toLowerCase().includes(qq)));
  }, [data, q, evFilter]);

  // Deep-link: open the exact animal passed from My Riders once it's loaded.
  useEffect(() => {
    if (!autoOpen || !data) return;
    const match = data.stock.find((s) => (s.stock || "").toLowerCase() === autoOpen.toLowerCase());
    if (match) setOpen(match.stock);
  }, [autoOpen, data]);

  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";

  return (
    <>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search any animal by name or number…" data-testid="stock-all-search"
          className={`w-full rounded-lg border pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#C8000F] ${inp}`} />
      </div>

      {data && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="stock-event-filter">
          <button onClick={() => setEvFilter("")} className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border ${!evFilter ? "bg-[#C8000F] text-white border-[#C8000F]" : (m ? "bg-white/5 text-slate-300 border-white/10" : "bg-white text-slate-600 border-[#D7DBE0]")}`}>All events</button>
          {data.events.map((ev) => (
            <button key={ev} onClick={() => setEvFilter(ev)} data-testid={`stock-ev-${ev.replace(/\s/g, "-")}`}
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border ${evFilter === ev ? "bg-[#C8000F] text-white border-[#C8000F]" : (m ? "bg-white/5 text-slate-300 border-white/10" : "bg-white text-slate-600 border-[#D7DBE0]")}`}>{ev}</button>
          ))}
          <span className="mx-1 text-slate-300">|</span>
          <button onClick={() => setSort("count")} data-testid="stock-sort-count" className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border ${sort === "count" ? "bg-[#14181F] text-white border-[#14181F]" : (m ? "bg-white/5 text-slate-300 border-white/10" : "bg-white text-slate-600 border-[#D7DBE0]")}`}>Most drawn</button>
          <button onClick={() => setSort("money")} data-testid="stock-sort-money" className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border ${sort === "money" ? "bg-[#3B6B1E] text-white border-[#3B6B1E]" : (m ? "bg-white/5 text-slate-300 border-white/10" : "bg-white text-slate-600 border-[#D7DBE0]")}`}>Most money</button>
          <button onClick={() => setSort("score")} data-testid="stock-sort-score" className={`text-[11px] font-semibold px-2.5 py-1 rounded-md border ${sort === "score" ? "bg-[#3B6B1E] text-white border-[#3B6B1E]" : (m ? "bg-white/5 text-slate-300 border-white/10" : "bg-white text-slate-600 border-[#D7DBE0]")}`}>Top marked</button>
        </div>
      )}

      {!data ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
      ) : (
        <>
          <div className="text-xs text-slate-400" data-testid="stock-all-count">{list.length} animals</div>
          <div className="space-y-2">
            {list.map((s, i) => {
              const isOpen = open === s.stock;
              return (
                <div key={i} className={`rounded-xl border ${card}`} data-testid={`stock-item-${i}`}>
                  <button onClick={() => setOpen(isOpen ? null : s.stock)} className="w-full flex items-center gap-3 p-3 text-left">
                    {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold text-sm ${title}`}>{s.stock}</div>
                      <div className="text-[11px] text-slate-400">{s.event}</div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {s.avg_score != null && <span className="text-[11px] font-bold px-2 py-1 rounded bg-[#3B6B1E]/15 text-[#3B6B1E]" data-testid={`stock-avg-${i}`}>avg {s.avg_score}</span>}
                      {s.total_won > 0 && <span className="text-[11px] font-bold px-2 py-1 rounded bg-[#E7F6E1] text-[#3B6B1E]" data-testid={`stock-won-${i}`}>{money(s.total_won)} won</span>}
                      <span className="text-[11px] font-bold px-2 py-1 rounded bg-[#C8000F]/15 text-[#C8000F]">drawn {s.count}×</span>
                    </div>
                  </button>
                  {isOpen && (
                    <div className={`px-3 pb-3 border-t ${m ? "border-white/5" : "border-[#EEF0F2]"}`}>
                      {s.riders?.length > 0 && (
                        <div className="pt-2.5">
                          <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1.5">Riders who've been on it</div>
                          <div className="flex flex-wrap gap-1.5">
                            {s.riders.map((rd, k) => (
                              <span key={k} className={`text-[11px] px-2 py-1 rounded-md ${m ? "bg-white/5 border border-white/10 text-slate-200" : "bg-[#F1F3F5] border border-[#E4E7EB] text-slate-700"}`}>
                                {rd.name}{rd.count > 1 && <span className="font-bold text-[#C8000F]"> ×{rd.count}</span>}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mt-3 mb-1">Every trip</div>
                      <div className="space-y-1.5">
                        {s.appearances.map((a, j) => (
                          <div key={j} className="flex items-center justify-between gap-2 text-xs">
                            <div className="min-w-0">
                              <span className={`font-medium ${title}`}>{a.contestant}</span>
                              <span className="text-slate-400"> · {a.rodeo_name}</span>
                              {ordinal(a.place) && <span className="ml-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#3B6B1E]/15 text-[#3B6B1E]">{ordinal(a.place)}</span>}
                              {a.value && <span className="ml-1.5 font-bold text-[#3B6B1E]" data-testid={`stock-score-${j}`}>{a.value}</span>}
                              {a.amount_won > 0 && <span className="ml-1.5 font-bold text-[#E68A2E]">{money(a.amount_won)}</span>}
                            </div>
                            <span className="text-slate-400 shrink-0">{a.performance_label}{a.performance_date ? ` · ${fmtDate(a.performance_date)}` : ""}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {list.length === 0 && <div className="text-center py-12 text-slate-400">No animals match your search.</div>}
          </div>
          <div className={`rounded-xl border border-dashed p-4 text-xs text-slate-400 ${m ? "border-white/15" : "border-[#D7DBE0]"}`} data-testid="stock-tendency-note">
            <div className={`flex items-center gap-2 text-sm font-semibold mb-1 ${title}`}><Info className="w-4 h-4 text-[#E68A2E]" /> About scores &amp; money</div>
            Scores/times and payouts come from the official CPRA results, matched to the animal each rider drew — so the "$ won" is the real money paid out on that horse or bull across every placing rider. Tendency notes ("always goes left") aren't in the data; tell me if you'd like to add your own per animal.
          </div>
        </>
      )}
    </>
  );
}

function ByRodeo({ m, q, setQ, inp, title }) {
  const [rodeos, setRodeos] = useState(null);
  const [selId, setSelId] = usePersistentState("rp_stock_rodeo", "");
  const [draw, setDraw] = useState(null);

  useEffect(() => {
    api.get("/rodeos").then((r) => {
      const withDraw = r.data.filter((x) => x.real_draw);
      setRodeos(withDraw);
      if (!selId && withDraw[0]) setSelId(withDraw[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selId) { setDraw(null); return; }
    setDraw(null);
    api.get(`/rodeos/${selId}/draw`).then((r) => setDraw(r.data)).catch(() => setDraw({ events: [] }));
  }, [selId]);

  const stockEvents = useMemo(() => (draw?.events || []).filter(hasStock), [draw]);
  const timedEvents = useMemo(() => (draw?.events || []).filter((e) => !hasStock(e)), [draw]);
  const filterRows = (rows) => {
    const qq = q.trim().toLowerCase();
    if (!qq) return rows;
    return rows.filter((r) => `${r.contestant || ""} ${r.stock || ""}`.toLowerCase().includes(qq));
  };

  const selRodeo = (rodeos || []).find((r) => r.id === selId);
  const card = m ? "bg-white/5 border-white/10" : "bg-white border-[#E4E7EB]";

  return (
    <>
      <div className="flex flex-col sm:flex-row gap-3">
        <select value={selId} onChange={(e) => setSelId(e.target.value)} data-testid="stock-rodeo-select"
          className={`flex-1 rounded-lg border px-3 py-2.5 text-sm focus:outline-none focus:border-[#C8000F] ${inp}`}>
          <option value="">Select a rodeo…</option>
          {(rodeos || []).map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <div className="relative sm:w-64">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a rider or animal…" data-testid="stock-search"
            className={`w-full rounded-lg border pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#C8000F] ${inp}`} />
        </div>
      </div>

      {selRodeo && <div className="text-xs text-slate-400">{selRodeo.city}, {selRodeo.state} · draw of {(draw?.events || []).reduce((n, e) => n + (e.total || 0), 0)} entries</div>}

      {rodeos === null || (selId && draw === null) ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C8000F]" /></div>
      ) : !selId ? (
        <div className="text-center py-16 text-slate-400">Pick a rodeo to see the stock draw.</div>
      ) : stockEvents.length === 0 && timedEvents.length === 0 ? (
        <div className="text-center py-16 text-slate-400">No draw posted yet for this rodeo.</div>
      ) : (
        <>
          {stockEvents.map((ev) => {
            const rows = filterRows(ev.rows);
            return (
              <section key={ev.event} data-testid={`stock-event-${ev.event.replace(/\s/g, "-")}`}>
                <div className={`text-sm font-display font-bold mb-2 ${title}`}>{ev.event} <span className="text-slate-400 font-normal">· {ev.total} up</span></div>
                <div className={`rounded-xl border overflow-hidden ${card}`}>
                  <div className={`grid grid-cols-[36px_1fr_1.4fr] gap-2 px-3 py-2 text-[10px] font-bold uppercase tracking-wide ${m ? "text-slate-500 bg-black/20" : "text-slate-400 bg-[#FAFBFC]"}`}>
                    <div>#</div><div>Contestant</div><div>Stock drawn</div>
                  </div>
                  {rows.length === 0 ? <div className="px-3 py-4 text-sm text-slate-400">No matches.</div>
                    : rows.map((r, i) => (
                      <div key={i} className={`grid grid-cols-[36px_1fr_1.4fr] gap-2 px-3 py-2.5 text-sm border-t ${m ? "border-white/5" : "border-[#EEF0F2]"}`} data-testid={`stock-row-${ev.event.replace(/\s/g, "-")}-${r.position}`}>
                        <div className="font-mono text-slate-400">{r.position}</div>
                        <div className={`font-semibold ${title}`}>{r.contestant}<div className="text-[11px] text-slate-400 font-normal">{r.performance_label}{r.performance_date ? ` · ${fmtDate(r.performance_date)}` : ""}</div></div>
                        <div className="font-medium text-[#C8000F]">
                          {r.stock}
                          {(r.result_value || r.amount_won > 0) && (
                            <div className="text-[11px] font-bold" data-testid={`draw-earn-${r.position}`}>
                              {ordinal(r.result_place) && <span className="text-[#3B6B1E] mr-1">{ordinal(r.result_place)}</span>}
                              {r.result_value && <span className="text-[#3B6B1E]">{r.result_value}</span>}
                              {r.amount_won > 0 && <span className="text-[#E68A2E] ml-1.5">{money(r.amount_won)}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </section>
            );
          })}

          {timedEvents.length > 0 && (
            <div className={`rounded-xl border border-dashed p-4 ${m ? "border-white/15" : "border-[#D7DBE0]"}`} data-testid="stock-timed-note">
              <div className={`flex items-center gap-2 text-sm font-semibold mb-1 ${title}`}><Info className="w-4 h-4 text-[#E68A2E]" /> Steer / cattle draw not published</div>
              <p className="text-xs text-slate-400 mb-2">The specific steer/calf each contestant draws in these timed events isn't on the public CPRA draw sheets — it's handled at the event secretary's office. We'll pull it in automatically if a public feed ever posts it.</p>
              <div className="flex flex-wrap gap-1.5">
                {timedEvents.map((e) => <span key={e.event} className={`text-[11px] px-2 py-1 rounded-md ${m ? "bg-white/5 text-slate-300 border border-white/10" : "bg-[#F1F3F5] text-slate-600 border border-[#E4E7EB]"}`}>{e.event}</span>)}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
